// The search box in the top bar. As you type it shows the pages and records that match; arrows move, Enter opens
// the highlighted one (or every result when nothing is highlighted), Esc closes. "/" anywhere puts you in it.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, request, type RecordRow } from "./api";
import { bcUrl } from "./config";
import { Icon } from "./icons";
import { findPages, type PageHit } from "./findPages";
import { useAccess } from "./security/store";
import { recordHref, typeLabel, typeNamed } from "./ui";

type Item = { kind: "page"; p: PageHit } | { kind: "record"; r: RecordRow } | { kind: "all" };

/** Records matching what was typed, a moment after typing stops. Null while it looks. */
export function useRecordSearch(q: string, delay = 200): RecordRow[] | null {
  const [rows, setRows] = useState<RecordRow[] | null>([]);
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRows([]); return; }
    setRows(null);
    let live = true;
    // A record whose number starts with what was typed comes first, then one whose title does, then the rest.
    const rank = (r: RecordRow) => { const x = t.toLowerCase(); return r.number.toLowerCase().startsWith(x) ? 0 : r.title.toLowerCase().startsWith(x) ? 1 : 2; };
    // A search that names a record type ("work orders") also brings that type's latest records, first.
    const type = typeNamed(t);
    const h = window.setTimeout(() => {
      Promise.all([api.search(t).catch(() => [] as RecordRow[]), type ? request<RecordRow[]>("GET", `/api/records?type=${encodeURIComponent(type)}`).catch(() => [] as RecordRow[]) : Promise.resolve([] as RecordRow[])])
        .then(([found, ofType]) => {
          if (!live) return;
          const first = ofType.slice(0, 25);
          setRows([...first, ...[...found].sort((a, b) => rank(a) - rank(b)).filter((r) => !first.some((x) => x.id === r.id))]);
        });
    }, delay);
    return () => { live = false; window.clearTimeout(h); };
  }, [q, delay]);
  return rows;
}

export function openPage(p: PageHit, nav: (to: string) => void) {
  if (p.to) nav(p.to);
  else if (p.bc) window.open(bcUrl(p.bc), "_blank", "noopener,noreferrer");
}

export function TopSearch() {
  const nav = useNavigate();
  const loc = useLocation();
  const access = useAccess();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const pages = findPages(q, access, 6);
  const records = useRecordSearch(q);
  const items: Item[] = [...pages.map((p) => ({ kind: "page" as const, p })), ...(records ?? []).slice(0, 5).map((r) => ({ kind: "record" as const, r })), ...(q.trim() ? [{ kind: "all" as const }] : [])];

  // A new page closes the list and clears the box.
  useEffect(() => { setOpen(false); setQ(""); setActive(-1); }, [loc.pathname, loc.search]);
  // "/" focuses the search, unless you're already typing somewhere.
  useEffect(() => {
    const k = (e: globalThis.KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || el.closest("input, textarea, select, [contenteditable='true']")) return;
      e.preventDefault(); input.current?.focus();
    };
    const out = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("keydown", k); document.addEventListener("mousedown", out);
    return () => { window.removeEventListener("keydown", k); document.removeEventListener("mousedown", out); };
  }, []);

  const go = (it: Item | undefined) => {
    const t = q.trim();
    // Enter on an exact record number or page name opens it; anything else shows every result.
    const exact = !it ? items.find((x) => (x.kind === "record" && x.r.number.toLowerCase() === t.toLowerCase()) || (x.kind === "page" && x.p.name.toLowerCase() === t.toLowerCase())) : undefined;
    if (exact) return go(exact);
    if (!it || it.kind === "all") { if (t) nav(`/search?q=${encodeURIComponent(t)}`); }
    else if (it.kind === "page") openPage(it.p, nav);
    else nav(recordHref(it.r.type, it.r.number));
    setOpen(false); input.current?.blur();
  };
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); go(active >= 0 ? items[active] : undefined); }
    else if (e.key === "Escape") { if (open && q) setOpen(false); else { setQ(""); input.current?.blur(); } }
  };
  const show = open && q.trim().length > 0;
  let i = -1;
  const row = (it: Item, body: ReactNode, k: string) => {
    i += 1; const n = i;
    return (
      <li key={k} id={`ts-${n}`} role="option" aria-selected={active === n} className={active === n ? "on" : ""}
        onMouseEnter={() => setActive(n)} onMouseDown={(e) => { e.preventDefault(); go(it); }}>{body}</li>
    );
  };

  return (
    <div className="topsearch" ref={box}>
      <form role="search" onSubmit={(e) => { e.preventDefault(); go(active >= 0 ? items[active] : undefined); }}>
        <Icon name="search" size={16} />
        <input ref={input} aria-label="Search for a page or record" placeholder="Search for a page or record" value={q}
          role="combobox" aria-expanded={show} aria-controls="ts-list" aria-autocomplete="list" aria-activedescendant={active >= 0 ? `ts-${active}` : undefined}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }} onFocus={() => setOpen(true)} onKeyDown={key} />
        {!q && <kbd className="ts-kbd" aria-hidden="true">/</kbd>}
      </form>
      {show && (
        <ul className="ts-pop" id="ts-list" role="listbox" aria-label="Search results">
          {pages.length > 0 && <li className="ts-h" role="presentation">Pages</li>}
          {pages.map((p) => row({ kind: "page", p }, (
            <><span className="ts-n">{p.name}{p.bc ? <span className="ts-ext"><Icon name="ext" size={11} /></span> : null}</span><span className="ts-w">{p.where}</span></>
          ), p.key))}
          {(records === null || records.length > 0) && <li className="ts-h" role="presentation">Records</li>}
          {records === null && <li className="ts-none" role="presentation">Looking…</li>}
          {(records ?? []).slice(0, 5).map((r) => row({ kind: "record", r }, (
            <><span className="ts-n"><span className="num">{r.number}</span> {r.title}</span><span className="ts-w">{typeLabel(r.type)}</span></>
          ), r.id))}
          {records !== null && !pages.length && !records.length && <li className="ts-none" role="presentation">Nothing matches “{q.trim()}”. Try a record number, a title, a lot or an item.</li>}
          {row({ kind: "all" }, <span className="ts-n">See all results for “{q.trim()}”</span>, "all")}
        </ul>
      )}
    </div>
  );
}
